import {
  Contract,
  ParticipationDecision,
  Tender,
  TenderPriority,
  TenderResult,
  TenderStatus,
  TenderWorkflowStage,
  LegalMatter,
} from '../types';
import { toFiniteNumber } from '../utils/money';

// Base URL da API — relativa por padrão ("/api").
// Em produção (Nginx HTTPS) e em dev via Vite, "/api" funciona via proxy.
// Se VITE_API_BASE_URL estiver definida, respeita; se não terminar em "/api",
// adiciona o sufixo (tolerante a valores tipo "http://ip:porta" do docker-compose).
const _raw = (import.meta as any).env?.VITE_API_BASE_URL ?? '/api';
const _normalized = String(_raw).replace(/\/+$/, '');
const API_URL = /\/api$/.test(_normalized) ? _normalized : `${_normalized}/api`;

// So manda o token para o nosso proprio servidor. A URL assinada do S3 ja traz a
// autorizacao na query; com o Bearer junto a AWS recusa o upload com 400.
const isOwnOrigin = (input: RequestInfo | URL) => {
  try {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return true;
  }
};

const apiFetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const headers = new Headers(init.headers || {});
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token && !headers.has('Authorization') && isOwnOrigin(input)) headers.set('Authorization', `Bearer ${token}`);
  const response = await globalThis.fetch(input, { ...init, headers });
  if (response.status === 401 && typeof localStorage !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined' && window.location.hash !== '#/login') window.location.hash = '/login';
  }
  return response;
};

const fetch = apiFetch;

// Helper: converter valor do enum para chave (ex: "Em Análise" -> "ANALYSIS")
export const getStatusKey = (statusValue: TenderStatus | string): string => {
  const statusMap: Record<string, string> = {
    'Aberta': 'OPEN',
    'Fechada': 'CLOSED',
    'Em Análise': 'ANALYSIS',
    'Ganha': 'WON',
    'Perdida': 'LOST',
    'Participar': 'PARTICIPATE',
    'Alta Prioridade': 'HIGH_PRIORITY',
    'Altíssima Prioridade': 'EXTREME_PRIORITY',
    'Não Participar': 'NO_GO',
    'Cotação Eletrônica': 'ELECTRONIC_QUOTE',
    'Dispensa Direta': 'DIRECT_WAIVER',
    'Randômica': 'RANDOM',
  };

  // Se já é uma chave válida, retorna como está
  if (Object.values(statusMap).includes(statusValue as string)) {
    return statusValue as string;
  }

  // Caso contrário, converte valor para chave
  return statusMap[statusValue as string] || 'ANALYSIS';
};

const STATUS_VALUES: Record<string, TenderStatus> = {
  OPEN: TenderStatus.OPEN,
  CLOSED: TenderStatus.CLOSED,
  ANALYSIS: TenderStatus.ANALYSIS,
  WON: TenderStatus.WON,
  LOST: TenderStatus.LOST,
  PARTICIPATE: TenderStatus.PARTICIPATE,
  HIGH_PRIORITY: TenderStatus.HIGH_PRIORITY,
  EXTREME_PRIORITY: TenderStatus.EXTREME_PRIORITY,
  NO_GO: TenderStatus.NO_GO,
  ELECTRONIC_QUOTE: TenderStatus.ELECTRONIC_QUOTE,
  DIRECT_WAIVER: TenderStatus.DIRECT_WAIVER,
  RANDOM: TenderStatus.RANDOM,
};

const normalizeTender = (tender: any): Tender => ({
  ...tender,
  status: STATUS_VALUES[String(tender?.status)] || tender?.status || TenderStatus.ANALYSIS,
  value: toFiniteNumber(tender?.value),
});

const normalizeContract = (contract: any): Contract => ({
  ...contract,
  value: toFiniteNumber(contract?.value),
  monthlyValue: contract?.monthlyValue == null ? undefined : toFiniteNumber(contract.monthlyValue),
  renewalHistory: (Array.isArray(contract?.renewalHistory) ? contract.renewalHistory : contract?.renewals || []).map((renewal: any) => ({
    ...renewal,
    adjustmentPercentage: renewal?.adjustmentPercentage == null ? undefined : Number(renewal.adjustmentPercentage),
    previousMonthlyValue: renewal?.previousMonthlyValue == null ? undefined : toFiniteNumber(renewal.previousMonthlyValue),
    newMonthlyValue: renewal?.newMonthlyValue == null ? undefined : toFiniteNumber(renewal.newMonthlyValue),
  })),
  totalMonths: contract?.totalMonths == null ? undefined : Number(contract.totalMonths),
  billedInstallments: Number(contract?.billedInstallments || 0),
});

const normalizeTenderResult = (result: any): TenderResult => ({
  ...result,
  ourBidValue: result?.ourBidValue == null ? null : toFiniteNumber(result.ourBidValue),
  winnerBidValue: result?.winnerBidValue == null ? null : toFiniteNumber(result.winnerBidValue),
  ourPlacement: result?.ourPlacement == null ? null : Number(result.ourPlacement),
  competitors: Array.isArray(result?.competitors) ? result.competitors.map((competitor: any) => ({
    ...competitor,
    placement: Number(competitor.placement),
    bidValue: competitor.bidValue == null ? null : toFiniteNumber(competitor.bidValue),
  })) : [],
  tender: result?.tender ? normalizeTender(result.tender) : undefined,
});

export const apiService = {
  async getLegalMatters(params: { category?: string; status?: string; search?: string } = {}): Promise<LegalMatter[]> {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    const response = await fetch(`${API_URL}/legal-matters${query.size ? `?${query}` : ''}`);
    if (!response.ok) throw new Error('Erro ao buscar registros jurídicos');
    return response.json();
  },

  async createLegalMatter(data: Partial<LegalMatter>): Promise<LegalMatter> {
    const response = await fetch(`${API_URL}/legal-matters`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Erro ao criar registro jurídico');
    return result;
  },

  async updateLegalMatter(id: string, data: Partial<LegalMatter>): Promise<LegalMatter> {
    const response = await fetch(`${API_URL}/legal-matters/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Erro ao atualizar registro jurídico');
    return result;
  },

  async deleteLegalMatter(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/legal-matters/${id}`, { method: 'DELETE' });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Erro ao excluir registro jurídico');
  },

  // Licitações
  async getTenders(): Promise<Tender[]> {
    const response = await fetch(`${API_URL}/tenders`);
    if (!response.ok) throw new Error('Erro ao buscar licitações');
    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeTender) : [];
  },

  async getWorkflowTenders(stage: TenderWorkflowStage): Promise<Tender[]> {
    const response = await fetch(`${API_URL}/tenders/workflow?stage=${encodeURIComponent(stage)}`);
    if (!response.ok) throw new Error('Erro ao buscar licitações desta etapa');
    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeTender) : [];
  },

  async favoriteExternalTender(externalTenderId: string): Promise<{ tender: Tender; alreadyFavorited: boolean }> {
    const response = await fetch(`${API_URL}/tenders/external/${encodeURIComponent(externalTenderId)}/favorite`, {
      method: 'POST',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao favoritar oportunidade');
    return {
      tender: normalizeTender(data.tender),
      alreadyFavorited: Boolean(data.alreadyFavorited),
    };
  },

  async getExternalTenders(view: 'ACTIVE' | 'FAVORITED' | 'ARCHIVED' | 'DISCARDED' | 'ALL' = 'ACTIVE'): Promise<import('../types').ExternalTender[]> {
    const response = await fetch(`${API_URL}/tenders/external?view=${encodeURIComponent(view)}`);
    if (!response.ok) throw new Error('Erro ao buscar oportunidades de licitação');
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  },

  async favoriteExternalTenders(ids: string[]): Promise<{
    requested: number;
    favorited: number;
    alreadyFavorited: number;
    processedIds: string[];
    errors: Array<{ id: string; error: string }>;
  }> {
    const response = await fetch(`${API_URL}/tenders/external/favorites/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao favoritar oportunidades');
    return data;
  },

  async archiveExternalTenders(ids: string[]): Promise<{
    requested: number;
    archived: number;
    processedIds: string[];
    errors: Array<{ id: string; error: string }>;
  }> {
    const response = await fetch(`${API_URL}/tenders/external/archives/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao descartar oportunidades');
    return data;
  },

  async updateExternalTenderParticipation(
    ids: string[],
    participationDecision: Extract<ParticipationDecision, 'PARTICIPATE' | 'DO_NOT_PARTICIPATE'>,
  ): Promise<{
    requested: number;
    updated: number;
    processedIds: string[];
    errors: Array<{ id: string; error: string }>;
  }> {
    const response = await fetch(`${API_URL}/tenders/external/participation/batch`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, participationDecision }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao atualizar participação das oportunidades');
    return data;
  },

  async restoreExternalTenders(ids: string[]): Promise<{
    requested: number;
    restored: number;
    processedIds: string[];
    errors: Array<{ id: string; error: string }>;
  }> {
    const response = await fetch(`${API_URL}/tenders/external/restores/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao restaurar oportunidades');
    return data;
  },

  async updateTenderQualification(
    id: string,
    qualification: Partial<{
      priority: TenderPriority;
      participationDecision: ParticipationDecision;
      documentsSeparated: boolean;
      documentsValidated: boolean;
      brandsValidated: boolean;
      modelsValidated: boolean;
      valuesValidated: boolean;
    }>,
  ): Promise<Tender> {
    const response = await fetch(`${API_URL}/tenders/${id}/qualification`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(qualification),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao atualizar a qualificação');
    return normalizeTender(data);
  },

  async setTenderResultTracking(id: string, enabled: boolean): Promise<Tender> {
    const response = await fetch(`${API_URL}/tenders/${id}/result-tracking`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao atualizar o acompanhamento do resultado');
    return normalizeTender(data);
  },

  async advanceTenderToParticipation(id: string): Promise<Tender> {
    const response = await fetch(`${API_URL}/tenders/${id}/advance-participation`, { method: 'POST' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const missing = Array.isArray(data.missing) && data.missing.length
        ? ` Pendências: ${data.missing.join(', ')}.`
        : '';
      throw new Error(`${data.error || 'Não foi possível avançar a licitação.'}${missing}`);
    }
    return normalizeTender(data);
  },

  async archiveTender(id: string): Promise<Tender> {
    const response = await fetch(`${API_URL}/tenders/${id}/archive`, { method: 'POST' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Não foi possível arquivar a licitação.');
    return normalizeTender(data);
  },

  async createTender(tender: Partial<Tender>): Promise<Tender> {
    // Converter status para chave do enum antes de enviar
    const payload = {
      ...tender,
      status: tender.status ? getStatusKey(tender.status) : 'ANALYSIS'
    };
    const response = await fetch(`${API_URL}/tenders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // try to parse backend error message
      try {
        const err = await response.json();
        const msg = err?.error || JSON.stringify(err);
        throw new Error(msg);
      } catch (e: any) {
        throw new Error(e?.message || `Erro ao criar licitação (${response.status})`);
      }
    }

    return normalizeTender(await response.json());
  },

  async updateTender(id: string, tender: Partial<Tender>): Promise<Tender> {
    // Converter status para chave do enum antes de enviar
    const payload = {
      ...tender,
      status: tender.status ? getStatusKey(tender.status) : undefined
    };

    const response = await fetch(`${API_URL}/tenders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Erro ao atualizar licitação');
    return normalizeTender(await response.json());
  },

  async deleteTender(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/tenders/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Erro ao deletar licitação');
  },

  async getTenderResults(): Promise<TenderResult[]> {
    const response = await fetch(`${API_URL}/tender-results`);
    if (!response.ok) throw new Error('Erro ao buscar resultados de licitações');
    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeTenderResult) : [];
  },

  async upsertTenderResult(tenderId: string, result: Partial<TenderResult>): Promise<TenderResult> {
    const response = await fetch(`${API_URL}/tenders/${tenderId}/result`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(result),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao salvar resultado da licitação');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao salvar resultado da licitação');
      }
    }
    return normalizeTenderResult(await response.json());
  },

  async getTenderById(id: string): Promise<Tender> {
    const response = await fetch(`${API_URL}/tenders/${id}`);
    if (!response.ok) throw new Error('Erro ao buscar licitação por id');
    return normalizeTender(await response.json());
  },

  // Contratos
  async getContracts(): Promise<Contract[]> {
    const response = await fetch(`${API_URL}/contracts`);
    if (!response.ok) throw new Error('Erro ao buscar contratos');
    const data = await response.json();
    return Array.isArray(data) ? data.map(normalizeContract) : [];
  },

  async getContractById(id: string): Promise<Contract> {
    const response = await fetch(`${API_URL}/contracts/${id}`);
    if (!response.ok) throw new Error('Erro ao buscar contrato');
    return normalizeContract(await response.json());
  },

  async createContract(contract: Partial<Contract>): Promise<Contract> {
    // Attach current user info from localStorage when available
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user && !(contract as any).userId) (contract as any).userId = user.id;
      if (user && !(contract as any).userName) (contract as any).userName = user.name;
      if (user && !(contract as any).userEmail) (contract as any).userEmail = user.email;
    } catch (e) {
      // ignore
    }
    const response = await fetch(`${API_URL}/contracts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contract),
    });
    if (!response.ok) {
      // Normalize error to include response-like shape so callers can inspect status/data
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        // Se não conseguir parsear, data fica null
        console.warn('Não foi possível parsear resposta de erro:', parseError);
      }

      // Sempre lançar objeto com response.status e response.data
      const error: any = new Error(data?.error || `Erro ${response.status}`);
      error.response = { status: response.status, data };
      throw error;
    }

    return normalizeContract(await response.json());
  },

  async updateContract(id: string, contract: Partial<Contract>): Promise<Contract> {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user && !(contract as any).userId) (contract as any).userId = user.id;
      if (user && !(contract as any).userName) (contract as any).userName = user.name;
      if (user && !(contract as any).userEmail) (contract as any).userEmail = user.email;
    } catch (e) {}
    const response = await fetch(`${API_URL}/contracts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contract),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        const msg = data?.error || JSON.stringify(data);
        throw new Error(`Erro ao atualizar contrato: ${msg}`);
      } catch (e) {
        throw new Error(`Erro ao atualizar contrato: ${response.status} ${response.statusText}`);
      }
    }
    return normalizeContract(await response.json());
  },

  async createContractAttachment(contractId: string, attachment: { name: string; url: string; type?: string | null; size?: number }): Promise<any> {
    const response = await fetch(`${API_URL}/contracts/${contractId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attachment),
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao criar anexo'); } catch (e) { throw e; }
    }
    return response.json();
  },

  async deleteContractAttachment(contractId: string, attachmentId: string): Promise<void> {
    const response = await fetch(`${API_URL}/contracts/${contractId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao deletar anexo'); } catch (e) { throw e; }
    }
  },

  async deleteContract(id: string): Promise<void> {
    // Send user info via headers so backend can log who deleted
    const headers: Record<string,string> = {};
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user) {
        headers['X-User-Id'] = String(user.id);
        headers['X-User-Name'] = String(user.name);
        if (user.email) headers['X-User-Email'] = String(user.email);
      }
    } catch (e) {}
    const response = await fetch(`${API_URL}/contracts/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error('Erro ao deletar contrato');
  },

  async createRenewal(contractId: string, renewal: { renewalDate?: string; newEndDate: string; adjustmentPercentage: number }): Promise<Contract> {
    const response = await fetch(`${API_URL}/contracts/${contractId}/renewals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(renewal),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao criar renovação');
    }
    return normalizeContract(await response.json());
  },

  // Empresas
  async getCompanies(): Promise<any[]> {
    const response = await fetch(`${API_URL}/companies`);
    if (!response.ok) throw new Error('Erro ao buscar empresas');
    return response.json();
  },

  async createCompany(company: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/companies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(company),
    });
    if (!response.ok) throw new Error('Erro ao criar empresa');
    return response.json();
  },

  async updateCompany(id: string, company: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/companies/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(company),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao atualizar empresa');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao atualizar empresa');
      }
    }
    return response.json();
  },

  async deleteCompany(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/companies/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        // Se houver detalhes sobre dependências, inclua-os no erro
        if (data.details) {
          const error: any = new Error(data.error || 'Erro ao deletar empresa');
          error.details = data.details;
          throw error;
        }
        throw new Error(data.error || 'Erro ao deletar empresa');
      } catch (e: any) {
        // Preserve details if present
        if (e.details) throw e;
        throw new Error(e.message || 'Erro ao deletar empresa');
      }
    }
  },

  // Usuários
  async getUsers(): Promise<any[]> {
    const response = await fetch(`${API_URL}/users`);
    if (!response.ok) throw new Error('Erro ao buscar usuários');
    return response.json();
  },

  // Lista para @menções: liberada para qualquer perfil (a de cima é só ADMIN/MANAGER)
  async getMentionableUsers(): Promise<any[]> {
    const response = await fetch(`${API_URL}/mentions/users`);
    if (!response.ok) throw new Error('Erro ao buscar usuários para menção');
    return response.json();
  },

  async createUser(user: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar usuário');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar usuário');
      }
    }
    return response.json();
  },

  async updateUser(id: string, user: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao atualizar usuário');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao atualizar usuário');
      }
    }
    return response.json();
  },

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar usuário');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao deletar usuário');
      }
    }
  },

  // Ordens de Compra (Empenhos)
  async getOrders(): Promise<any[]> {
    const response = await fetch(`${API_URL}/orders`);
    if (!response.ok) throw new Error('Erro ao buscar ordens');
    return response.json();
  },

  async getOrderById(id: string): Promise<any> {
    const response = await fetch(`${API_URL}/orders/${id}`);
    if (!response.ok) throw new Error('Erro ao buscar ordem');
    return response.json();
  },

  async createOrder(order: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(order),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar ordem');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar ordem');
      }
    }
    return response.json();
  },

  async updateOrder(id: string, order: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(order),
    });

    if (!response.ok) {
      // Try to parse response body to extract backend error details
      let data: any = null;
      try {
        data = await response.json();
      } catch (e) {
        // ignore parse errors
      }

      const err: any = new Error(data?.error || `Erro ao atualizar ordem (${response.status})`);
      // Attach response data for richer debugging in the caller
      err.response = { status: response.status, data };
      throw err;
    }

    return response.json();
  },

  async deleteOrder(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/orders/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar ordem');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao deletar ordem');
      }
    }
  },

  // Order attachments & comments
  async createOrderAttachment(orderId: string, attachment: { name: string; url: string; type?: string; size?: number; uploadedBy?: string; uploadedByName?: string }): Promise<any> {
    const response = await fetch(`${API_URL}/orders/${orderId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attachment),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar anexo');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar anexo');
      }
    }
    return response.json();
  },

  async deleteOrderAttachment(orderId: string, attachmentId: string): Promise<void> {
    const response = await fetch(`${API_URL}/orders/${orderId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar anexo');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao deletar anexo');
      }
    }
  },

  // Tender attachments
  async createTenderAttachment(tenderId: string, attachment: { name: string; url: string; type?: string; size?: number; uploadedBy?: string; uploadedByName?: string }): Promise<any> {
    const response = await fetch(`${API_URL}/tenders/${tenderId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attachment),
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao criar anexo'); } catch (e: any) { throw e; }
    }
    return response.json();
  },

  async getTenderAttachments(tenderId: string): Promise<any[]> {
    const response = await fetch(`${API_URL}/tenders/${tenderId}/attachments`);
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao buscar anexos'); } catch (e: any) { throw e; }
    }
    return response.json();
  },

  async uploadTenderAttachmentBase64(tenderId: string, file: File): Promise<any> {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
      reader.readAsDataURL(file);
    });
    const response = await fetch(`${API_URL}/tenders/${tenderId}/attachments/upload-base64`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: file.name, type: file.type, base64 }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Erro ao enviar o comprovante');
    return data;
  },

  async deleteTenderAttachment(tenderId: string, attachmentId: string): Promise<void> {
    const response = await fetch(`${API_URL}/tenders/${tenderId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao deletar anexo'); } catch (e: any) { throw e; }
    }
  },

  async getOrderComments(orderId: string): Promise<any[]> {
    const response = await fetch(`${API_URL}/orders/${orderId}/comments`);
    if (!response.ok) throw new Error('Erro ao buscar comentários');
    return response.json();
  },

  async createOrderComment(orderId: string, comment: { content: string; userId?: string; userName?: string; meta?: any }): Promise<any> {
    const response = await fetch(`${API_URL}/orders/${orderId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(comment),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar comentário');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar comentário');
      }
    }
    return response.json();
  },

  async deleteOrderComment(orderId: string, commentId: string): Promise<void> {
    const response = await fetch(`${API_URL}/orders/${orderId}/comments/${commentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar comentário');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao deletar comentário');
      }
    }
  },

  // Documentos Oficiais
  async getDocuments(): Promise<any[]> {
    const response = await fetch(`${API_URL}/documents`);
    if (!response.ok) throw new Error('Erro ao buscar documentos');
    return response.json();
  },

  async createDocument(doc: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(doc),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar documento');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar documento');
      }
    }
    return response.json();
  },

  async updateDocument(id: string, doc: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/documents/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(doc),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao atualizar documento');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao atualizar documento');
      }
    }
    return response.json();
  },

  async deleteDocument(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/documents/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao deletar documento');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao deletar documento');
      }
    }
  },

  // Technical Certificates (Atestados) - nova API dedicada
  async getTechnicalCertificates(): Promise<any[]> {
    const response = await fetch(`${API_URL}/technical-certificates`);
    if (!response.ok) throw new Error('Erro ao buscar technical certificates');
    return response.json();
  },

  async createTechnicalCertificate(cert: Partial<any>): Promise<any> {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user && !(cert as any).userId) (cert as any).userId = user.id;
      if (user && !(cert as any).userName) (cert as any).userName = user.name;
      if (user && !(cert as any).userEmail) (cert as any).userEmail = user.email;
    } catch (e) {}
    const response = await fetch(`${API_URL}/technical-certificates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cert),
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao criar technical certificate'); } catch (e: any) { throw e; }
    }
    return response.json();
  },

  async updateTechnicalCertificate(id: string, cert: Partial<any>): Promise<any> {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user && !(cert as any).userId) (cert as any).userId = user.id;
      if (user && !(cert as any).userName) (cert as any).userName = user.name;
      if (user && !(cert as any).userEmail) (cert as any).userEmail = user.email;
    } catch (e) {}
    const response = await fetch(`${API_URL}/technical-certificates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cert),
    });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao atualizar technical certificate'); } catch (e: any) { throw e; }
    }
    return response.json();
  },

  async deleteTechnicalCertificate(id: string): Promise<void> {
    const headers: Record<string,string> = {};
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      if (user) {
        headers['X-User-Id'] = String(user.id);
        headers['X-User-Name'] = String(user.name);
        if (user.email) headers['X-User-Email'] = String(user.email);
      }
    } catch (e) {}
    const response = await fetch(`${API_URL}/technical-certificates/${id}`, { method: 'DELETE', headers });
    if (!response.ok) {
      try { const data = await response.json(); throw new Error(data.error || 'Erro ao deletar technical certificate'); } catch (e: any) { throw e; }
    }
  },

  // --- OFFICIAL COMMUNICATIONS ---
  async getCommunications() {
    const response = await fetch(`${API_URL}/communications`);
    if (!response.ok) throw new Error('Erro ao buscar comunicações');
    return response.json();
  },

  async createCommunication(data: any) {
    const response = await fetch(`${API_URL}/communications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      try {
        const err = await response.json();
        throw new Error(err?.error || JSON.stringify(err));
      } catch (e) {
        const text = await response.text().catch(() => '');
        throw new Error(`Erro ao criar comunicação: ${response.status} ${response.statusText} ${text}`);
      }
    }
    return response.json();
  },

  async updateCommunication(id: string, data: any) {
    const response = await fetch(`${API_URL}/communications/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      try {
        const responseData = await response.json();
        throw new Error(responseData.error || 'Erro ao atualizar comunicação');
      } catch (e) {
        throw new Error('Erro ao atualizar comunicação');
      }
    }
    return response.json();
  },

  async deleteCommunication(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/communications/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      try {
        const responseData = await response.json();
        throw new Error(responseData.error || 'Erro ao deletar comunicação');
      } catch (e) {
        throw new Error('Erro ao deletar comunicação');
      }
    }
  },

  // Portais
  async getPortals(): Promise<any[]> {
    const response = await fetch(`${API_URL}/portals`);
    if (!response.ok) throw new Error('Erro ao buscar portais');
    return response.json();
  },

  async createPortal(portal: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/portals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(portal),
    });
    if (!response.ok) throw new Error('Erro ao criar portal');
    return response.json();
  },

  async updatePortal(id: string, portal: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/portals/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(portal),
    });
    if (!response.ok) throw new Error('Erro ao atualizar portal');
    return response.json();
  },

  async deletePortal(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/portals/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Erro ao deletar portal');
  },

  // Templates
  async getTemplates(): Promise<any[]> {
    const response = await fetch(`${API_URL}/templates`);
    if (!response.ok) throw new Error('Erro ao buscar templates');
    return response.json();
  },

  async createTemplate(template: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    });
    if (!response.ok) throw new Error('Erro ao criar template');
    return response.json();
  },

  async updateTemplate(id: string, template: Partial<any>): Promise<any> {
    const response = await fetch(`${API_URL}/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    });
    if (!response.ok) throw new Error('Erro ao atualizar template');
    return response.json();
  },

  async deleteTemplate(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/templates/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Erro ao deletar template');
  },

  // Produtos
  async getProducts(params?: { brand?: string; category?: string; q?: string }): Promise<any[]> {
    const queries: string[] = [];
    if (params) {
      if (params.brand) queries.push(`brand=${encodeURIComponent(params.brand)}`);
      if (params.category) queries.push(`category=${encodeURIComponent(params.category)}`);
      if (params.q) queries.push(`q=${encodeURIComponent(params.q)}`);
    }
    const url = `${API_URL}/products${queries.length ? '?' + queries.join('&') : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Erro ao buscar produtos');
    return response.json();
  },

  async createProduct(product: any): Promise<any> {
    const response = await fetch(`${API_URL}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao criar produto');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao criar produto');
      }
    }
    return response.json();
  },

  async updateProduct(id: string, product: any): Promise<any> {
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao atualizar produto');
      } catch (e: any) {
        throw new Error(e.message || 'Erro ao atualizar produto');
      }
    }
    return response.json();
  },

  async deleteProduct(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/products/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Erro ao deletar produto');
  },

  async downloadDatasheet(productId: string): Promise<any> {
    const response = await fetch(`${API_URL}/products/${productId}/datasheet`);
    if (!response.ok) throw new Error('Erro ao baixar datasheet');
    return response.json();
  },

  // Agenda (agregado de prazos)
  async getAgenda(): Promise<any[]> {
    const response = await fetch(`${API_URL}/agenda`);
    if (!response.ok) throw new Error('Erro ao buscar agenda');
    return response.json();
  },

  // Audit Logs
  async getAuditLogs(userId?: string, limit: number = 100): Promise<any[]> {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    params.append('limit', String(limit));
    const response = await fetch(`${API_URL}/audit-logs?${params}`);
    if (!response.ok) throw new Error('Erro ao buscar logs de auditoria');
    return response.json();
  },

  async getAuditLogsByResource(resourceType: string, resourceId: string): Promise<any[]> {
    const response = await fetch(`${API_URL}/audit-logs/resource/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`);
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao buscar logs de auditoria por recurso');
      } catch (e: any) {
        throw new Error(e?.message || 'Erro ao buscar logs de auditoria por recurso');
      }
    }
    return response.json();
  },

  async getAuditLogsByResourceBatch(resourceType: string, ids: string[]): Promise<Record<string, any[]>> {
    const response = await fetch(`${API_URL}/audit-logs/resource/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resourceType, ids }),
    });
    if (!response.ok) {
      try {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao buscar logs de auditoria em batch');
      } catch (e: any) {
        throw new Error(e?.message || 'Erro ao buscar logs de auditoria em batch');
      }
    }
    const json = await response.json();
    return json.results || {};
  },

  async createAuditLog(log: { action: string; details: string; type?: string; userId?: string; userName: string }): Promise<any> {
    const response = await fetch(`${API_URL}/audit-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(log),
    });
    if (!response.ok) throw new Error('Erro ao criar log de auditoria');
    return response.json();
  },

  // AI Sessions
  async getAISessions(userId?: string): Promise<any[]> {
    const params = userId ? `?userId=${userId}` : '';
    const response = await fetch(`${API_URL}/ai-sessions${params}`);
    if (!response.ok) throw new Error('Erro ao buscar sessões de IA');
    return response.json();
  },

  async createAISession(session: { title: string; tenderId?: string; userId?: string }): Promise<any> {
    const response = await fetch(`${API_URL}/ai-sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(session),
    });
    if (!response.ok) throw new Error('Erro ao criar sessão de IA');
    return response.json();
  },

  async updateAISession(id: string, data: { title: string }): Promise<any> {
    const response = await fetch(`${API_URL}/ai-sessions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Erro ao atualizar sessão de IA');
    return response.json();
  },

  async deleteAISession(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/ai-sessions/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Erro ao deletar sessão de IA');
  },

  async createAIMessage(message: { sessionId: string; role: string; content: string; attachments?: any }): Promise<any> {
    const response = await fetch(`${API_URL}/ai-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(message),
    });
    if (!response.ok) throw new Error('Erro ao criar mensagem de IA');
    return response.json();
  },

  // User Preferences
  async getUserPreferences(userId: string): Promise<any[]> {
    const response = await fetch(`${API_URL}/preferences?userId=${userId}`);
    if (!response.ok) throw new Error('Erro ao buscar preferências');
    return response.json();
  },

  async saveUserPreference(pref: { userId: string; key: string; value: string }): Promise<any> {
    const response = await fetch(`${API_URL}/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pref),
    });
    if (!response.ok) throw new Error('Erro ao salvar preferência');
    return response.json();
  },

  async deleteUserPreference(userId: string, key: string): Promise<void> {
    const response = await fetch(`${API_URL}/preferences/${userId}/${key}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Erro ao deletar preferência');
  },

  // --- S3 / ARQUIVOS ---
  async getPresignedUploadUrl(key: string, contentType: string): Promise<{ uploadUrl: string; downloadUrl: string; key: string }> {
    const response = await fetch(`${API_URL}/s3/presign-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, contentType }),
    });
    if (!response.ok) throw new Error('Erro ao gerar URL de upload');
    return response.json();
  },

  async getPresignedDownloadUrl(key: string): Promise<{ downloadUrl: string }> {
    // URL assinada pelo S3: abre em aba nova sem depender do cabecalho de sessao,
    // que um link em href nao consegue enviar. O endpoint ja devolve HTTPS, que
    // era o motivo do proxy usado antes aqui.
    const response = await fetch(`${API_URL}/s3/presign-download?key=${encodeURIComponent(key)}`);
    if (!response.ok) throw new Error('Erro ao gerar URL de download');
    return response.json();
  },

  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!response.ok) throw new Error('Erro ao fazer upload do arquivo para o S3');
  },

  // Extrair dados de certidões/documentos com IA
  async extractCertificateData(base64: string, fileName: string): Promise<any> {
    const response = await fetch(`${API_URL}/extract-certificate-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64, fileName }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erro ao extrair dados: ${errorText}`);
    }
    return response.json();
  },

  // --- EXTRAÇÃO DE DADOS DO PDF ---
  async extractDataFromPDF(file: File): Promise<{ name: string; brand: string; model: string; description: string; specifications?: string }> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await fetch(`${API_URL}/extract-pdf-data`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao extrair dados do PDF' }));
      throw new Error(error.error || 'Erro ao extrair dados do PDF');
    }

    return response.json();
  },

  async extractContractFromPDF(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await fetch(`${API_URL}/extract-contract-data`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao extrair dados do contrato' }));
      throw new Error(error.error || 'Erro ao extrair dados do contrato');
    }

    return response.json();
  },
  async extractOrderFromPDF(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await fetch(`${API_URL}/extract-order-data`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao extrair dados da ordem' }));
      throw new Error(error.error || 'Erro ao extrair dados da ordem');
    }

    return response.json();
  },
  async extractTenderFromPDF(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await fetch(`${API_URL}/extract-tender-data`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao extrair dados da licitação' }));
      throw new Error(error.error || 'Erro ao extrair dados da licitação');
    }

    return response.json();
  },
  async analyzeTenderDocument(base64: string, prompt: string, history?: any[]): Promise<{ text: string }> {
    const response = await fetch(`${API_URL}/analyze-tender`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64, prompt, history })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao analisar edital' }));
      throw new Error(error.error || 'Erro ao analisar edital');
    }
    return response.json();
  },

  async extractNotificationFromPDF(base64: string): Promise<{ type: string; deadline: string; summary: string }> {
    const response = await fetch(`${API_URL}/extract-notification-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64 })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao extrair notificação' }));
      throw new Error(error.error || 'Erro ao extrair notificação');
    }
    return response.json();
  },

  // Upload de arquivo de contrato
  async uploadContractFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/contracts/upload-file`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao fazer upload do arquivo' }));
      throw new Error(error.error || 'Erro ao fazer upload do arquivo');
    }

    const result = await response.json();
    return result.fileUrl; // Retorna a chave S3
  },
};
